import React from 'react';

import CircleIcon from '@mui/icons-material/Circle';
import { Box, Link, Tooltip, Typography } from '@sunwisesoftware/sunwise-ui';
import { useTranslation } from 'react-i18next';

import { PROPOSAL_STATUS } from 'common/constants';
import { useBreakpoint } from 'common/hooks';
import {
    arraytoDictionary,
    getProjectStatusOptions,
    getStatusProposalLabel,
} from 'common/utils/helpers';

import { getLandCategories } from '../helpers';

import ProposalStatusIcon from './ProposalStatusIcon';

const ProductColumns = () => {
    const { t } = useTranslation();
    const breakpoint = useBreakpoint();

    const nameColumn = [
        {
            align: 'left',
            display: 'flex',
            field: 'name',
            flex: 1,
            headerName: t('Proposal'),
            minWidth: 180,
            renderCell: (params) => {
                const { row } = params;
                if (row.render_status === PROPOSAL_STATUS.GENERATED)
                    return (
                        <Link
                            href={`/proposals/preview/${row.id}`}
                            underline="hover"
                            sx={{ textAlign: 'left', color: '#424242', py: 1 }}
                        >
                            <Typography fontWeight="bold" variant="caption">
                                {row.name}
                            </Typography>
                        </Link>
                    );
                return (
                    <Typography fontWeight="bold" variant="caption">
                        {row.name}
                    </Typography>
                );
            },
        },
    ];

    let columns = [
        ...nameColumn,
        {
            align: 'left',
            display: 'flex',
            field: 'project',
            flex: 1,
            headerName: t('Project'),
            minWidth: 250,
            renderCell: (params) => {
                const { row } = params;
                const statusDictionary = arraytoDictionary(
                    getProjectStatusOptions(),
                    'value',
                );
                return (
                    <Box alignItems="center" display="flex" gap={1}>
                        <Tooltip title={statusDictionary[row?.status]?.label}>
                            <CircleIcon
                                sx={{
                                    color: statusDictionary[row?.status]
                                        ?.primary_color,
                                    fontSize: 15,
                                }}
                            />
                        </Tooltip>
                        <Typography variant="caption">
                            {row?.project_name}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            align: 'left',
            display: 'flex',
            field: 'render_status',
            flex: 1,
            headerName: t('Status'),
            minWidth: 180,
            renderCell: (params) => {
                const { row } = params;
                const isGenerated = row?.render_status === 'generated';
                return (
                    <Box alignItems="center" display="flex" gap={1}>
                        <ProposalStatusIcon isGenerated={isGenerated} />
                        <Typography variant="caption">
                            {getStatusProposalLabel(isGenerated)}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            align: 'left',
            display: 'flex',
            field: 'contact',
            flex: 1,
            headerName: t('Contact'),
            minWidth: 180,
        },
        {
            align: 'left',
            display: 'flex',
            field: 'company',
            flex: 1,
            headerName: t('Company'),
            minWidth: 180,
        },
        {
            align: 'left',
            display: 'flex',
            field: 'country',
            flex: 1,
            headerName: t('Country'),
            minWidth: 180,
        },
        {
            align: 'left',
            display: 'flex',
            field: 'state',
            flex: 1,
            headerName: t('State'),
            minWidth: 180,
        },
        {
            align: 'left',
            display: 'flex',
            field: 'user',
            flex: 1,
            headerName: t('User'),
            minWidth: 180,
        },
        {
            align: 'left',
            display: 'flex',
            field: 'system',
            flex: 1,
            headerName: t('System'),
            minWidth: 180,
        },
        {
            align: 'left',
            display: 'flex',
            field: 'category',
            flex: 1,
            headerName: t('Roughness'),
            minWidth: 180,
            renderCell: (params) => {
                const { row } = params;
                const categoriesDictionary = arraytoDictionary(
                    getLandCategories(),
                    'value',
                );
                return (
                    <Tooltip title={categoriesDictionary[row?.category]?.label}>
                        <Box alignItems="center" display="flex" gap={1}>
                            <img
                                src={categoriesDictionary[row?.category]?.image}
                                width={20}
                            />
                            <Typography variant="body2">
                                R{row?.category}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            align: 'left',
            display: 'flex',
            field: 'panels',
            flex: 1,
            headerName: t('Panel', { count: 2 }),
            minWidth: 180,
        },
        {
            align: 'left',
            display: 'flex',
            field: 'system_size',
            flex: 1,
            headerName: 'kW',
            minWidth: 180,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography variant="caption">
                        {row?.system_size} KW
                    </Typography>
                );
            },
        },

        {
            align: 'left',
            display: 'flex',
            field: 'created_at',
            headerName: t('Discharge date'),
            minWidth: 180,
        },
    ];

    if (['xs', 'sm'].includes(breakpoint)) columns = nameColumn;

    return columns;
};

ProductColumns.propTypes = {};

export default ProductColumns;
